<!--
  FileDropZone.vue

  A surface that takes dropped files and queues them for upload (useUploader),
  outlining itself while files are dragged over it. With no slot content it is
  a dashed "Drop files or browse" box; with content, it wraps it. `browse()`
  opens the file picker for the same target.
-->
<template>
  <div
    class="file-drop-zone"
    :class="{ 'is-drag-over': isDragOver, 'is-bare': !$slots.default }"
    @drop.prevent.stop="!disabled && handleDrop($event)"
    @dragover.prevent="!disabled && handleDragOver($event)"
    @dragenter.prevent="!disabled && handleDragEnter($event)"
    @dragleave.prevent="!disabled && handleDragLeave($event)"
  >
    <slot :browse="browse" :is-drag-over="isDragOver">
      <span class="icon text-muted"><font-awesome-icon icon="cloud-upload-alt" /></span>
      <span class="text-7 text-muted">
        {{ isDragOver ? hoverLabel : "Drop files or" }}
        <a v-if="!isDragOver" @click="browse">browse</a>
      </span>
    </slot>
    <div v-if="isDragOver && $slots.default" class="overlay">{{ hoverLabel }}</div>
    <input
      ref="input"
      type="file"
      :accept="pickerAccept"
      :multiple="multiple"
      hidden
      @change="onPicked"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import type { AssetView } from "@auger/core/fields"
import { useUploader } from "@/composables/useUploader"
import type { UploadTarget } from "@/stores/uploads"

const props = withDefaults(
  defineProps<{
    target?: UploadTarget
    /** What a drop does, shown while dragging: "Drop to add to Board materials". */
    label?: string
    accept?: string
    multiple?: boolean
    /** Takes no drops; a drop on it is swallowed rather than opening the file. */
    disabled?: boolean
    /** The one kind of file it takes: the picker filters to it, drops refuse others. */
    accepts?: AssetView
  }>(),
  {
    target: () => ({}),
    label: undefined,
    accept: undefined,
    multiple: true,
    disabled: false,
    accepts: undefined,
  }
)

const { isDragOver, handleDragEnter, handleDragLeave, handleDragOver, handleDrop, processFiles } =
  useUploader(
    () => props.target,
    () => props.accepts
  )

// A `document` is every file that isn't media, which `accept` can't spell, so the
// picker shows all files for it and the drop check does the refusing.
const pickerAccept = computed(
  () =>
    props.accept ??
    (props.accepts && props.accepts !== "document" ? `${props.accepts}/*` : undefined)
)

const hoverLabel = computed(() => props.label ?? "Drop to upload")

const input = ref<HTMLInputElement | null>(null)

function browse() {
  input.value?.click()
}

function onPicked(event: Event) {
  const el = event.target as HTMLInputElement
  processFiles(Array.from(el.files ?? []))
  el.value = ""
}

defineExpose({ browse })
</script>

<style>
.file-drop-zone {
  position: relative;

  &.is-bare {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    min-height: 3.5rem;
    padding: 0.75rem;
    border: 1px dashed var(--auger-border-hover);
    border-radius: var(--auger-radius);
  }

  &.is-drag-over.is-bare {
    border: 2px dashed var(--auger-primary);
    background: var(--auger-primary-soft);
  }

  > .overlay {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed var(--auger-primary);
    border-radius: var(--auger-radius);
    background: color-mix(in srgb, var(--auger-bg) 85%, transparent);
    color: var(--auger-primary-dark);
    font-weight: 600;
    pointer-events: none;
  }
}
</style>
